import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  IconButton,
  InputAdornment,
  Button,
  Fab,
  Collapse,
  Popover,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import TaskIcon from "@mui/icons-material/Task";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getMeeting } from "../services/meetingService";
import { getTasks } from "../services/taskService";
import { Meeting, Task } from "../types";
import ItemCard, { ItemWithSource } from "../components/Cards/ItemCard";
import {
  TaskStatusKey,
  MeetingStatusKey,
  taskStatusColors,
  meetingStatusColors,
} from "../utils/statusColors";
import { alpha } from "@mui/material";
import Chip from "@mui/material/Chip";
import Notification from "../components/Common/Notification";
import { CircularProgress } from "@mui/material";
import { getFromCache } from "../storage/cacheManager";
import {
  shouldLoadData,
  setLoading,
  clearLoading,
  getLoadingPromise,
} from "../utils/dataLoader";
import { useNetworkStatus } from "../hooks/useNetworkStatus";

const TASKS_FILTERS_STORAGE_KEY = "tasks_filters";

type TypeKey = "all" | "task" | "meeting";
type StatusKey = "all" | TaskStatusKey | MeetingStatusKey;
type AssigneeKey = "all" | string;

const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  const [dataLoaded, setDataLoaded] = useState(false);

  const initialFilters = sessionStorage.getItem(TASKS_FILTERS_STORAGE_KEY)
    ? JSON.parse(sessionStorage.getItem(TASKS_FILTERS_STORAGE_KEY)!)
    : {
        typeFilter: "all",
        search: "",
        statusFilter: ["all"],
        assigneeFilter: "all",
      };

  const [search, setSearch] = useState<string>(initialFilters.search || "");
  const [typeFilter, setTypeFilter] = useState<TypeKey>(
    initialFilters.typeFilter || "all",
  );
  const [statusFilter, setStatusFilter] = useState<StatusKey[]>(
    initialFilters.statusFilter || ["all"],
  );
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeKey>(
    initialFilters.assigneeFilter || "all",
  );
  const [page, setPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);
  const createButtonRef = useRef<HTMLButtonElement>(null);
  const createFabRef = useRef<HTMLButtonElement>(null);
  const { isOnline } = useNetworkStatus();
  const rowsPerPage = 10;

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const defaultUserName = user?.name || t("tasks.currentUser");

  useEffect(() => {
    sessionStorage.setItem(
      TASKS_FILTERS_STORAGE_KEY,
      JSON.stringify({ typeFilter, search, statusFilter, assigneeFilter }),
    );
  }, [typeFilter, search, statusFilter, assigneeFilter]);

  const typeOptions: { value: TypeKey; label: string }[] = [
    { value: "all", label: t("typeOptions.all") },
    { value: "task", label: t("typeOptions.task") },
    { value: "meeting", label: t("typeOptions.meeting") },
  ];

  const taskStatusMap: Record<TaskStatusKey, string> = {
    "Not Started": "not_started",
    Started: "started",
    Completed: "completed",
    Deferred: "deferred",
    Сanceled: "canceled",
  };

  const meetingStatusMap: Record<MeetingStatusKey, string> = {
    Planned: "planned",
    Held: "held",
    "Not Held": "not_held",
  };

  const statusOptions: { value: StatusKey; label: string; color?: string }[] = [
    { value: "all", label: t("typeOptions.all") },
    ...(typeFilter === "task" || typeFilter === "all"
      ? Object.keys(taskStatusColors).map((key) => ({
          value: key as TaskStatusKey,
          label: t(`statuses.task.${taskStatusMap[key as TaskStatusKey]}`),
          color: taskStatusColors[key as TaskStatusKey]?.color,
        }))
      : []),
    ...(typeFilter === "meeting" || typeFilter === "all"
      ? Object.keys(meetingStatusColors).map((key) => ({
          value: key as MeetingStatusKey,
          label: t(
            `statuses.meeting.${meetingStatusMap[key as MeetingStatusKey]}`,
          ),
          color: meetingStatusColors[key as MeetingStatusKey]?.color,
        }))
      : []),
  ];

  const assigneeOptions: { value: AssigneeKey; label: string }[] = [
    { value: "all", label: t("typeOptions.all") },
    { value: defaultUserName, label: defaultUserName },
    ...Array.from(
      new Set([
        ...(tasks || []).map((task) => task.assignedUserName || ""),
        ...(meetings || []).map((meeting) => meeting.assignedUserName || ""),
      ]),
    )
      .filter((name) => name && name !== defaultUserName)
      .map((name) => ({ value: name, label: name })),
  ];

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        const userId = user?.id || "";

        const cachedTasks = await getFromCache<any>("tasks:list");
        const cachedMeetings = await getFromCache<any>("meetings:list");

        const cachedTaskList = Array.isArray(cachedTasks)
          ? cachedTasks
          : cachedTasks?.list || [];
        const cachedMeetingList = Array.isArray(cachedMeetings)
          ? cachedMeetings
          : cachedMeetings?.list || [];

        if (!cancelled) {
          setTasks(cachedTaskList);
          setMeetings(cachedMeetingList);
        }

        if (!navigator.onLine) {
          setDataLoaded(true);
          return;
        }

        const needTasks = await shouldLoadData("tasks", null);
        const needMeetings = await shouldLoadData("meetings", null);

        const promises: Promise<any>[] = [];

        if (needTasks) {
          const existingPromise = getLoadingPromise("tasks");
          if (existingPromise) {
            promises.push(existingPromise);
          } else {
            const tasksPromise = getTasks(userId);
            setLoading("tasks", tasksPromise);
            promises.push(
              tasksPromise.finally(() => {
                clearLoading("tasks");
              }),
            );
          }
        } else {
          promises.push(Promise.resolve({ list: cachedTaskList }));
        }

        if (needMeetings) {
          const existingPromise = getLoadingPromise("meetings");
          if (existingPromise) {
            promises.push(existingPromise);
          } else {
            const meetingsPromise = getMeeting(userId);
            setLoading("meetings", meetingsPromise);
            promises.push(
              meetingsPromise.finally(() => {
                clearLoading("meetings");
              }),
            );
          }
        } else {
          promises.push(Promise.resolve({ list: cachedMeetingList }));
        }

        const [taskResponse, meetingResponse] = await Promise.all(promises);
        if (cancelled) return;

        const taskList = Array.isArray(taskResponse)
          ? taskResponse
          : taskResponse?.list || [];
        const meetingList = Array.isArray(meetingResponse)
          ? meetingResponse
          : meetingResponse?.list || [];

        setTasks(taskList);
        setMeetings(meetingList);
        setDataLoaded(true);
      } catch (err: any) {
        if (err.response?.status === 403) {
          console.warn("403 Forbidden — используем кэш.");
        } else {
          console.error(err);
        }
        setTasks([]);
        setMeetings([]);
        setDataLoaded(true);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const items: ItemWithSource[] =
    typeFilter === "all"
      ? [
          ...(tasks || []).map(
            (task) =>
              ({
                ...task,
                source: "task",
                parentId: task.taskParentId || undefined,
              }) as ItemWithSource,
          ),
          ...(meetings || []).map(
            (m) => ({ ...m, source: "meeting" }) as ItemWithSource,
          ),
        ]
      : typeFilter === "task"
        ? (tasks || []).map(
            (task) =>
              ({
                ...task,
                source: "task",
                parentId: task.taskParentId || undefined,
              }) as ItemWithSource,
          )
        : (meetings || []).map(
            (m) => ({ ...m, source: "meeting" }) as ItemWithSource,
          );

  const filtered = items.filter((item) => {
    const q = search.toLowerCase();
    const matchesSearch =
      item.name.toLowerCase().includes(q) ||
      Boolean(item.description?.toLowerCase().includes(q));
    const matchesStatus =
      statusFilter.includes("all") || statusFilter.includes(item.status);
    const matchesAssignee =
      assigneeFilter === "all" || item.assignedUserName === assigneeFilter;
    return matchesSearch && matchesStatus && matchesAssignee;
  });

  const pageCount = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const getStatusConfig = (status: string, source: "task" | "meeting") => {
    const normalized = status.toLowerCase().replace(/\s/g, "_");
    if (source === "task") {
      const key = status as TaskStatusKey;
      const text = t(`statuses.task.${normalized}`) || status;
      return {
        color: taskStatusColors[key]?.color || "#ccc",
        translation: text,
      };
    } else {
      const key = status as MeetingStatusKey;
      const text = t(`statuses.meeting.${normalized}`) || status;
      return {
        color: meetingStatusColors[key]?.color || "#ccc",
        translation: text,
      };
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleClearSearch = () => {
    setSearch("");
    setPage(1);
  };

  const shadow = "0px 2px 3.6px rgba(0, 0, 0, 0.36)";

  if (!dataLoaded) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 0, md: 2 }, position: "relative" }}>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
        >
          {t("tasks.header")}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          sx={{
            p: 0.5,
            bgcolor: "#E0E6F0",
            color: "#4467A5",
            border: "1px solid #4467A5",
            borderRadius: "8px",
            boxShadow: shadow,
            "&:hover": { bgcolor: "#D5DCE8" },
          }}
          aria-label={
            isFilterOpen ? t("tasks.closeSearch") : t("tasks.filterButton")
          }
        >
          <FilterAltIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: { xs: "none", sm: "block" }, mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ fontSize: "2rem" }}>
          {t("tasks.header")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            placeholder={t("tasks.searchPlaceholder")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ flex: 1, maxWidth: 400 }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t("tasks.typeLabel")}</InputLabel>
            <Select
              label={t("tasks.typeLabel")}
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as TypeKey);
                setPage(1);
              }}
            >
              {typeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 300 }}>
            <InputLabel>{t("tasks.detail.statusLabel")}</InputLabel>
            <Select
              label={t("tasks.detail.statusLabel")}
              multiple
              value={statusFilter}
              onChange={(e) => {
                const value = e.target.value as StatusKey[];

                if (value.includes("all") && value.length === 1) {
                  setStatusFilter(["all"]);
                } else if (value.includes("all") && value.length > 1) {
                  setStatusFilter(value.filter((v) => v !== "all"));
                } else if (value.length === 0) {
                  setStatusFilter(["all"]);
                } else {
                  setStatusFilter(value);
                }
                setPage(1);
              }}
              renderValue={(selected) =>
                selected
                  .map(
                    (value) =>
                      statusOptions.find((opt) => opt.value === value)?.label,
                  )
                  .filter(Boolean)
                  .join(", ")
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    },
                  },
                },
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: option.color || "#ccc",
                      marginRight: 1,
                    }}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t("tasks.columnNames.assignee")}</InputLabel>
            <Select
              label={t("tasks.columnNames.assignee")}
              value={assigneeFilter}
              onChange={(e) => {
                setAssigneeFilter(e.target.value as AssigneeKey);
                setPage(1);
              }}
            >
              {assigneeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            ref={createButtonRef}
            variant="contained"
            onClick={async (e) => {
              const targetElement = e.currentTarget;
              const { available, reason } =
                await import("../utils/offlineGuard").then((m) =>
                  m.isOfflineActionAvailable("create", t),
                );
              if (!available) {
                setError(
                  reason ||
                    t("offline.createUnavailable") ||
                    "Создание недоступно в оффлайн режиме",
                );
                return;
              }
              setAnchorEl(targetElement);
            }}
            disabled={!isOnline}
            sx={{ textTransform: "none", boxShadow: shadow }}
          >
            {t("tasks.createButtonText")}
          </Button>
        </Box>
      </Box>
      <Collapse in={isFilterOpen} timeout="auto">
        <Box
          mb={2}
          sx={{
            display: { xs: "flex", sm: "none" },
            flexDirection: "column",
            gap: 1,
          }}
        >
          <TextField
            placeholder={t("tasks.searchPlaceholder")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small">
            <InputLabel>{t("tasks.typeLabel")}</InputLabel>
            <Select
              label={t("tasks.typeLabel")}
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as TypeKey);
                setPage(1);
              }}
            >
              {typeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>{t("tasks.detail.statusLabel")}</InputLabel>
            <Select
              label={t("tasks.detail.statusLabel")}
              multiple
              value={statusFilter}
              onChange={(e) => {
                const value = e.target.value as StatusKey[];
                if (value.includes("all") && value.length === 1) {
                  setStatusFilter(["all"]);
                } else if (value.includes("all") && value.length > 1) {
                  setStatusFilter(value.filter((v) => v !== "all"));
                } else if (value.length === 0) {
                  setStatusFilter(["all"]);
                } else {
                  setStatusFilter(value);
                }
                setPage(1);
              }}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    alignItems: "center",
                    minHeight: 24,
                    "@media (max-width:600px)": {
                      minHeight: 24,
                      maxHeight: 120,
                    },
                  }}
                >
                  {selected.map((value) => {
                    const option = statusOptions.find(
                      (opt) => opt.value === value,
                    );
                    const color = option?.color || "#bbb";
                    const label = option?.label || value;

                    return (
                      <Chip
                        key={value}
                        label={label}
                        size="small"
                        sx={{
                          bgcolor: alpha(color, 0.18),
                          color: "rgba(0, 0, 0, 0.87)",
                          border: "none",
                          fontWeight: 500,
                          mr: 0.5,
                          mb: 0.5,
                          ".MuiChip-label": { px: 1 },
                        }}
                        onDelete={
                          value !== "all"
                            ? (e) => {
                                if (
                                  e &&
                                  typeof e.stopPropagation === "function"
                                )
                                  e.stopPropagation();
                                setStatusFilter((prev) => {
                                  const next = prev.filter((v) => v !== value);
                                  return next.length ? next : ["all"];
                                });
                                setPage(1);
                              }
                            : undefined
                        }
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root.Mui-selected": {
                      backgroundColor: "rgba(255, 165, 0, 0.2)",
                    },
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: "rgba(255, 165, 0, 0.2)",
                    },
                  },
                },
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: option.color || "#ccc",
                      marginRight: 1,
                    }}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>{t("tasks.columnNames.assignee")}</InputLabel>
            <Select
              label={t("tasks.columnNames.assignee")}
              value={assigneeFilter}
              onChange={(e) => {
                setAssigneeFilter(e.target.value as AssigneeKey);
                setPage(1);
              }}
            >
              {assigneeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Collapse>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <MenuItem
          onClick={() => {
            navigate("/task/new");
            handleClosePopover();
          }}
        >
          <ListItemIcon>
            <TaskIcon fontSize="small" sx={{ color: "#4467A5" }} />
          </ListItemIcon>
          <ListItemText primary={t("typeOptions.task")} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/meeting/new");
            handleClosePopover();
          }}
        >
          <ListItemIcon>
            <MeetingRoomIcon fontSize="small" sx={{ color: "#4467A5" }} />
          </ListItemIcon>
          <ListItemText primary={t("typeOptions.meeting")} />
        </MenuItem>
      </Popover>
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Box
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            p: { xs: 1, sm: 2 },
            bgcolor: "background.paper",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {[
              t("tasks.columnNames.name"),
              t("tasks.columnNames.assignee"),
              t("tasks.columnNames.startDate"),
              t("tasks.columnNames.endDate"),
              t("tasks.columnNames.status"),
            ].map((label, idx) => (
              <Box
                key={idx}
                sx={{ flex: "1", textAlign: idx === 0 ? "left" : "center" }}
              >
                <Typography variant="body1">{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {paginated.length ? (
          paginated.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              getStatusConfig={getStatusConfig}
              onStatusChange={async () => {
                try {
                  const userId = user?.id || "";
                  const [taskResponse, meetingResponse] = await Promise.all([
                    getTasks(userId),
                    getMeeting(userId),
                  ]);

                  const taskList = Array.isArray(taskResponse)
                    ? taskResponse
                    : taskResponse?.list || [];
                  const meetingList = Array.isArray(meetingResponse)
                    ? meetingResponse
                    : meetingResponse?.list || [];

                  setTasks(taskList);
                  setMeetings(meetingList);
                } catch (err: any) {
                  console.error("Failed to reload tasks and meetings:", err);
                }
              }}
            />
          ))
        ) : (
          <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
            {t("tasks.noData")}
          </Typography>
        )}
      </Box>
      {pageCount > 1 && (
        <Box mt={2} display="flex" justifyContent="center">
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
          />
        </Box>
      )}
      <Fab
        ref={createFabRef}
        color="primary"
        size="small"
        onClick={async (e) => {
          e.stopPropagation();
          const targetElement = e.currentTarget;
          const { available, reason } =
            await import("../utils/offlineGuard").then((m) =>
              m.isOfflineActionAvailable("create", t),
            );
          if (!available) {
            setError(
              reason ||
                t("offline.createUnavailable") ||
                "Создание недоступно в оффлайн режиме",
            );
            return;
          }
          setAnchorEl(targetElement);
        }}
        disabled={!isOnline}
        sx={{
          display: { xs: "flex", sm: "none" },
          position: "fixed",
          bottom: 62,
          right: 16,
          border: "1px solid #4467A5",
          borderRadius: "8px",
          bgcolor: "#4467A5",
          color: "#fff",
          "&:hover": { bgcolor: "#385894" },
          boxShadow: shadow,
        }}
        aria-label={t("tasks.createButton")}
      >
        <AddIcon />
      </Fab>
      {error && (
        <Notification
          open={!!error}
          message={error}
          severity="warning"
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
      )}
    </Box>
  );
};

export default Tasks;
